import { createElement, type ReactNode } from 'react';
import { type Id, type ToastOptions, type ToastPosition, toast } from 'react-toastify';

type OpenNotificationParams = {
  /**
   * Override react-toastify's default 5s auto-close. Pass a positive number of
   * ms to dismiss sooner, or `false` to keep the toast open until clicked.
   * Ignored for `type: 'progress'` (those are driven by `undoableTimeout`).
   */
  autoClose?: number | false;
  cancelMutation?: () => void;
  description?: string;
  key?: string;
  message: string;
  type: 'success' | 'error' | 'progress';
  undoableTimeout?: number;
};

type OpenCustomNotificationParams = {
  /**
   * Arbitrary toast body. The caller owns the layout and any interactive
   * controls (the built-in message/description/cancel affordances are skipped).
   */
  content: ReactNode;
  /**
   * Auto-close delay in ms, or `false` (default) to keep it open until the
   * caller dismisses it via `close(key)` or the content dismisses itself.
   */
  autoClose?: number | false;
  key?: string;
  /** Override the container's default placement (e.g. 'bottom-center'). */
  position?: ToastPosition;
  /** Toast styling variant (e.g. 'info'); defaults to the neutral toast. */
  variant?: ToastOptions['type'];
};

type NotificationProvider = {
  close: (key?: string) => void;
  open: (params: OpenNotificationParams) => void;
  openCustom: (params: OpenCustomNotificationParams) => void;
};

type ToastFactory = (content: ReactNode, options?: ToastOptions) => Id;

const getToastFactory = (type: OpenNotificationParams['type']): ToastFactory => {
  if (type === 'success') {
    return (content, options) => toast.success(content, options);
  }
  if (type === 'error') {
    return (content, options) => toast.error(content, options);
  }
  return (content, options) => toast.loading(content, options);
};

const createToastContent = ({
  message,
  description,
  onCancel,
}: {
  message: string;
  description?: string;
  onCancel?: () => void;
}) =>
  createElement(
    'div',
    null,
    createElement('div', null, message),
    description ? createElement('div', null, description) : null,
    onCancel
      ? createElement(
          'button',
          {
            onClick: onCancel,
            type: 'button',
          },
          'Cancel'
        )
      : null
  );

const openNotification: NotificationProvider['open'] = ({
  autoClose,
  cancelMutation,
  description,
  key,
  message,
  type,
  undoableTimeout,
}) => {
  const toastFactory = getToastFactory(type);

  // eslint-disable-next-line prefer-const
  let toastId: Id | undefined;

  const handleCancel = () => {
    cancelMutation?.();
    const idToDismiss = key ?? toastId;
    if (idToDismiss !== undefined) {
      toast.dismiss(idToDismiss);
    }
  };

  const content = createToastContent({
    description,
    message,
    onCancel: cancelMutation ? handleCancel : undefined,
  });

  const options: ToastOptions = {
    autoClose: type === 'progress' ? (undoableTimeout === undefined ? false : undoableTimeout) : autoClose,
    closeButton: type !== 'progress',
    closeOnClick: type !== 'progress',
    draggable: type !== 'progress',
    hideProgressBar: true,
    toastId: key,
  };

  toastId = toastFactory(content, options);
};

const openCustomNotification: NotificationProvider['openCustom'] = ({
  content,
  autoClose = false,
  key,
  position,
  variant,
}) => {
  toast(content, {
    autoClose,
    closeButton: false,
    closeOnClick: false,
    draggable: false,
    hideProgressBar: true,
    position,
    toastId: key,
    type: variant,
  });
};

const closeNotification: NotificationProvider['close'] = (key) => {
  if (key) {
    toast.dismiss(key);
    return;
  }

  toast.dismiss();
};

export const notificationProvider: NotificationProvider = {
  close: closeNotification,
  open: openNotification,
  openCustom: openCustomNotification,
};

export const useNotification = () => notificationProvider;
